import { LocalizedTextSchema } from "@uzverse/core";
import { z } from "zod";
/** A setting's input kind, derived from its Zod schema (`SettingDef.schema` is not serializable). */
export const SettingKindSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("boolean") }),
    z.object({
        type: z.literal("number"),
        min: z.number().optional(),
        max: z.number().optional(),
        integer: z.boolean().optional(),
    }),
    z.object({ type: z.literal("string"), maxLength: z.number().optional() }),
    z.object({
        type: z.literal("enum"),
        options: z.array(z.object({ value: z.string(), label: LocalizedTextSchema })),
    }),
    /** Anything the form cannot edit (objects, arrays); shown read-only. */
    z.object({ type: z.literal("json") }),
]);
export const SettingValueSchema = z.union([z.boolean(), z.number(), z.string(), z.null()]);
/** A module-declared setting in serializable form, with its current value. */
export const SettingDescriptorSchema = z.object({
    key: z.string().min(1),
    /** Module id (the key's first segment), for grouping. */
    module: z.string().min(1),
    scope: z.enum(["world", "company", "user"]),
    level: z.enum(["basic", "advanced"]),
    label: LocalizedTextSchema,
    description: LocalizedTextSchema.optional(),
    kind: SettingKindSchema,
    /** Whether `null` is a valid value (e.g. "no budget limit"). */
    nullable: z.boolean(),
    value: SettingValueSchema,
    default: SettingValueSchema,
    /** False when the caller may view but not change it. */
    editable: z.boolean(),
});
export const SettingsResponseSchema = z.object({ settings: z.array(SettingDescriptorSchema) });
export const SetSettingBodySchema = z.object({ value: z.unknown() });
function branches(s) {
    return s.anyOf ?? s.oneOf ?? [s];
}
const isNull = (s) => s.type === "null" || (s.const === null && s.type === undefined);
/** Derives the form kind of a Zod schema via its JSON Schema. */
export function settingKindOf(schema) {
    const json = z.toJSONSchema(schema, { unrepresentable: "any", io: "input" });
    const all = branches(json);
    const nullable = all.some(isNull) || (Array.isArray(json.type) && json.type.includes("null"));
    const rest = all.filter((b) => !isNull(b));
    const main = rest.length === 1 ? rest[0] : null;
    if (!main)
        return { kind: { type: "json" }, nullable };
    const type = Array.isArray(main.type) ? main.type.find((t) => t !== "null") : main.type;
    if (main.enum && main.enum.every((v) => typeof v === "string")) {
        return {
            kind: {
                type: "enum",
                options: main.enum.map((v) => ({ value: v, label: { tr: v, en: v } })),
            },
            nullable,
        };
    }
    if (type === "boolean")
        return { kind: { type: "boolean" }, nullable };
    if (type === "number" || type === "integer") {
        const min = main.minimum ?? main.exclusiveMinimum;
        const max = main.maximum ?? main.exclusiveMaximum;
        return {
            kind: {
                type: "number",
                ...(min !== undefined ? { min } : {}),
                ...(max !== undefined ? { max } : {}),
                ...(type === "integer" ? { integer: true } : {}),
            },
            nullable,
        };
    }
    if (type === "string") {
        return {
            kind: {
                type: "string",
                ...(main.maxLength !== undefined ? { maxLength: main.maxLength } : {}),
            },
            nullable,
        };
    }
    return { kind: { type: "json" }, nullable };
}
const asValue = (v) => v === null || typeof v === "boolean" || typeof v === "number" || typeof v === "string"
    ? v
    : JSON.stringify(v);
/** Serializes a `SettingDef` (Zod) and its current value for the settings UI. */
export function toSettingDescriptor(def, value, editable) {
    const { kind, nullable } = settingKindOf(def.schema);
    return {
        key: def.key,
        module: def.key.split(".")[0] ?? def.key,
        scope: def.scope,
        level: def.level,
        label: def.label,
        ...(def.description ? { description: def.description } : {}),
        kind,
        nullable,
        value: asValue(value),
        default: asValue(def.default),
        editable: editable && kind.type !== "json",
    };
}
//# sourceMappingURL=settings.js.map